import {
  DimensionType,
  DIMENSION_LABELS,
  DimensionGroupingConfig,
} from '@/types/rebateAnalytics';

/**
 * Generate an intelligent auto-generated table name that clarifies X vs Y dimensions
 * Format: "By [X dimensions] across [Y dimensions]"
 * Adds asterisks (*) to dimensions that have grouping configs
 */
export function generateTableName(
  primaryXDimension: DimensionType,
  secondaryXDimension: DimensionType | undefined,
  primaryYDimension: DimensionType | undefined,
  secondaryYDimension: DimensionType | undefined,
  dimensionGroupingConfig?: Partial<
    Record<
      Exclude<DimensionType, DimensionType.TIME | DimensionType.RULE_TYPE>,
      DimensionGroupingConfig
    >
  >,
): string {
  // Helper to check if a dimension has grouping config
  const hasGrouping = (dimension: DimensionType | undefined): boolean => {
    if (
      !dimension ||
      dimension === DimensionType.TIME ||
      !dimensionGroupingConfig
    ) {
      return false;
    }
    return dimension in dimensionGroupingConfig;
  };

  // Helper to format dimension label with asterisk if grouped
  const formatDimensionLabel = (dimension: DimensionType): string => {
    const label = DIMENSION_LABELS[dimension];
    return hasGrouping(dimension) ? `${label}*` : label;
  };

  // Build X axis description (columns)
  const xParts: string[] = [formatDimensionLabel(primaryXDimension)];
  if (secondaryXDimension) {
    xParts.push(formatDimensionLabel(secondaryXDimension));
  }
  const xDescription = xParts.join(' × ');

  // Build Y axis description (rows)
  const yParts: string[] = [];
  if (primaryYDimension) {
    yParts.push(formatDimensionLabel(primaryYDimension));
  }
  if (secondaryYDimension) {
    yParts.push(formatDimensionLabel(secondaryYDimension));
  }
  const yDescription = yParts.length > 0 ? yParts.join(' × ') : 'All';

  // Format: "By [X dimensions] || [Y dimensions]"
  return `By ${xDescription} || ${yDescription}`;
}
